import { apiRequest } from "./api";

// Subscription/return-credit packages - an optional, discounted alternative to
// the per-vehicle Stripe service fee (paymentDetailsService.js). Buying a
// package draws down return credits used to file returns instead of paying
// the per-vehicle fee on a given return - both payment paths coexist
// permanently, filer's choice per return. Every amount below is computed
// server-side; never trust or recompute a price/discount on the frontend.

// GET /subscriptions/packages -> [{ categoryId, code, name, description, returnsPerUnit,
//   packages: [{ packageId, packageQuantity, returnsIncluded, unitPrice, discountType,
//                discountValue, baseAmount, discountAmount, finalAmount }] }]
// Public (no auth) - plain product pricing, nothing user-specific. Called both from the
// logged-in Dashboard > Subscriptions page and the public marketing Pricing page.
export async function getSubscriptionPackages() {
  return apiRequest(`/subscriptions/packages`, { auth: false });
}

// GET /subscriptions/my -> UserSubscriptionResponse[] (only subscriptionStatus = ACTIVE)
export async function getMySubscriptions() {
  return apiRequest(`/subscriptions/my`);
}

// GET /subscriptions/history -> UserSubscriptionResponse[] (every purchase, newest first)
export async function getSubscriptionHistory() {
  return apiRequest(`/subscriptions/history`);
}

// GET /subscriptions/{id} -> UserSubscriptionResponse
export async function getSubscriptionById(id) {
  return apiRequest(`/subscriptions/${id}`);
}

// GET /subscriptions/eligibility?returnId= -> { eligible, options: [{ subscriptionId,
//   categoryCode, categoryName, returnsRemaining, minVehicles, maxVehicles }] } - one entry
// per usable subscription that can pay for THIS SPECIFIC return (a filer may hold more than
// one at once, e.g. a SOLO and a DUO-2 bought separately, but only credits matching this
// return's filing type are offered - the Amendments plan for an amended return, every other
// plan for original/final, never mixed). Display-only - never trusted to gate filing itself;
// the backend enforces that independently when the return is actually generated/submitted.
export async function getSubscriptionEligibility(returnId) {
  return apiRequest(`/subscriptions/eligibility?returnId=${returnId}`);
}

// GET /subscriptions/topup-eligibility?returnId= -> same shape as getSubscriptionEligibility,
// but for the ADDITIONAL service fee owed when a rejected return has extra vehicles added
// beyond what it was originally paid for (see paymentDetailsService.js's
// getServiceFeeTopUpConfig) - lets the filer use another return credit to cover that instead
// of paying by card.
export async function getSubscriptionTopUpEligibility(returnId) {
  return apiRequest(`/subscriptions/topup-eligibility?returnId=${returnId}`);
}

// POST /subscriptions/purchase/config?packageId= -> { publishableKey, amount (cents),
//   currency, packageId, categoryCode, packageQuantity, returnsIncluded, baseAmount,
//   discountAmount, finalAmount (dollar strings) }
export async function getPurchaseConfig(packageId) {
  return apiRequest(`/subscriptions/purchase/config?packageId=${packageId}`, { method: "POST" });
}

// POST /subscriptions/purchase/intent?packageId= -> { clientSecret, publishableKey, userSubscriptionId }
export async function createPurchaseIntent(packageId) {
  return apiRequest(`/subscriptions/purchase/intent?packageId=${packageId}`, { method: "POST" });
}

// GET /subscriptions/purchase/{id}/status -> UserSubscriptionResponse
// Poll this after confirming payment; paymentStatus becomes SUCCESS once the
// Stripe webhook (or this endpoint's own reconcile) finalizes the charge.
export async function getPurchaseStatus(userSubscriptionId) {
  return apiRequest(`/subscriptions/purchase/${userSubscriptionId}/status`);
}

// POST /returns/{returnId}/subscription-usage/reserve  body: { userSubscriptionId }
// -> UserSubscriptionResponse (with the reservation already reflected in returnsRemaining)
// Reserving does NOT itself pay for the return - the credit is only permanently
// spent when the return actually clears generation/submission.
export async function reserveSubscriptionCredit(returnId, userSubscriptionId) {
  return apiRequest(`/returns/${returnId}/subscription-usage/reserve`, {
    method: "POST",
    body: { userSubscriptionId },
  });
}

// POST /returns/{returnId}/subscription-usage/topup-reserve  body: { userSubscriptionId }
// -> UserSubscriptionResponse - the top-up counterpart of reserveSubscriptionCredit: covers
// the ADDITIONAL fee for vehicles added beyond what this return was originally paid for,
// instead of paying it by card.
export async function reserveSubscriptionTopUpCredit(returnId, userSubscriptionId) {
  return apiRequest(`/returns/${returnId}/subscription-usage/topup-reserve`, {
    method: "POST",
    body: { userSubscriptionId },
  });
}

// GET /returns/{returnId}/subscription-usage/topup-status -> { status, id } - the latest
// subscription_topup_usage row for this return (that table isn't one-row-per-return, unlike
// the original credit, so this is always the most recent one). PaymentDetailsStep.jsx calls
// this right after reserveSubscriptionTopUpCredit to get that row's own id, so the
// confirmation screen's receipt points at THIS top-up instead of the return's original
// fee/credit payment.
export async function getSubscriptionTopUpUsageStatus(returnId) {
  return apiRequest(`/returns/${returnId}/subscription-usage/topup-status`);
}

// POST /returns/{returnId}/subscription-usage/release -> 204, idempotent (safe even if nothing was reserved)
export async function releaseSubscriptionCredit(returnId) {
  return apiRequest(`/returns/${returnId}/subscription-usage/release`, { method: "POST" });
}

// GET /returns/{returnId}/subscription-usage -> { status: "RESERVED" | "USED" | "RELEASED" | null }
// status USED means this return was already paid for with a subscription credit (not the
// Stripe service fee) - PaymentDetailsStep checks this alongside getServiceFeeStatus so a
// rejected return resubmit correctly skips payment options regardless of which of the two
// payment paths it originally went through.
export async function getSubscriptionUsageStatus(returnId) {
  return apiRequest(`/returns/${returnId}/subscription-usage`);
}

// GET /subscriptions/upsell -> { show } - whether to show the payment page's "you don't
// have a subscription yet" banner. True only for a filer with zero subscription purchase
// history who hasn't already closed the banner (dismissSubscriptionUpsell below).
export async function getSubscriptionUpsell() {
  return apiRequest(`/subscriptions/upsell`);
}

// POST /subscriptions/upsell/dismiss -> 204. Permanent for the account - the banner won't
// be shown again on any future filing, not just this browser session.
export async function dismissSubscriptionUpsell() {
  return apiRequest(`/subscriptions/upsell/dismiss`, { method: "POST" });
}
